import type { RasterSourceSpecification, StyleSpecification } from 'maplibre-gl';

export type MapStyleId = 'streets' | 'satellite' | 'hybrid';

/** Cycle order for the 3-way style toggle -- also the flyout panel's display order. */
export const MAP_STYLE_ORDER: MapStyleId[] = ['streets', 'satellite', 'hybrid'];

export const MAP_STYLE_LABEL_KEYS: Record<MapStyleId, string> = {
  streets: 'map.style.streets',
  satellite: 'map.style.satellite',
  hybrid: 'map.style.hybrid',
};

export const STREETS_URL = 'https://tiles.openfreemap.org/styles/liberty';

const SATELLITE_SOURCE: RasterSourceSpecification = {
  type: 'raster',
  tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
  tileSize: 256,
  attribution: 'Imagery &copy; Esri',
  maxzoom: 18,
};

export function buildSatelliteStyle(): StyleSpecification {
  return {
    version: 8,
    sources: { satellite: SATELLITE_SOURCE },
    layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
  };
}

/**
 * Builds a "hybrid" style: satellite imagery underneath the Streets basemap's roads/labels.
 *
 * Lifts the actual line + symbol layers (roads, borders, place/POI labels) out of the Streets
 * vector style and drops everything else (background, landcover/landuse/water fills, the
 * natural_earth raster) so satellite imagery shows through where those fills would have been.
 */
export async function buildHybridStyle(): Promise<StyleSpecification> {
  const response = await fetch(STREETS_URL);
  const style = (await response.json()) as StyleSpecification;
  const overlayLayers = style.layers.filter((layer) => layer.type === 'line' || layer.type === 'symbol');

  return {
    ...style,
    sources: { ...style.sources, satellite: SATELLITE_SOURCE },
    layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }, ...overlayLayers],
  };
}
